import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, Trash2, Lock, MessageSquare, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

// const PROJECT_OPTIONS = [
//   "Mobile App",
//   "Web App",
//   "Admin Panel",
//   "Backend API",
//   "Desktop App",
//   "Customer Portal",
//   "Other"
// ];

export default function TaskDialog({ open, onOpenChange, task, onSaved }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [dbProjects, setDbProjects] = useState([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);

  const [form, setForm] = useState({
    title: "", description: "", assignee_ids: [], priority: "medium", status: "todo", deadline: "", project: "",
  });
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  
  // Mention suggestion states
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(-1);
  const textareaRef = useRef(null);

  const isEdit = !!task;
  const isManager = ["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(user?.role);
  // Developer can only edit own tasks
  const isCreator = task && task.created_by === user?.id;
  const isAssigned = task && task.assignee_ids?.includes(user?.id);
  const canEdit = isManager || isAssigned || isCreator;
  
  // Whether the user is allowed to edit the main fields (title, desc, project, priority, deadline)
  const canEditFields = isManager || (!isEdit) || isCreator;

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await API.get("/projects");
      setDbProjects(data || []);
    } catch (e) {
      console.error("Failed to fetch projects", e);
    }
  }, []);

  useEffect(() => {
    API.get("/users").then((res) => setUsers(res.data)).catch(() => {});
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setCreatingProject(true);
    try {
      const { data } = await API.post("/projects", { name: newProjectName.trim() });
      setDbProjects((prev) => {
        const updated = [...prev, data];
        return updated.sort((a, b) => a.name.localeCompare(b.name));
      });
      setForm((prev) => ({ ...prev, project: data.name }));
      setNewProjectName("");
      setShowNewProject(false);
      toast.success("Proyek berhasil ditambahkan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal membuat proyek");
    } finally {
      setCreatingProject(false);
    }
  };

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        assignee_ids: task.assignee_ids || [],
        priority: task.priority || "medium",
        status: task.status || "todo",
        deadline: task.deadline || "",
        project: task.project || "",
      });
      setActiveTab("details");
    } else {
      setForm({ 
        title: "", 
        description: "", 
        assignee_ids: !isManager && user?.id ? [user.id] : [], 
        priority: "medium", 
        status: "todo", 
        deadline: "", 
        project: "" 
      });
      setActiveTab("details");
    }
  }, [task, open, isManager, user?.id]);

  const fetchComments = useCallback(async () => {
    if (!task?.id) return;
    try {
      const { data } = await API.get(`/tasks/${task.id}/comments`);
      setComments(data);
    } catch { /* ignore */ }
  }, [task?.id]);

  useEffect(() => {
    if (open && task?.id) fetchComments();
  }, [open, task?.id, fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!isEdit && form.assignee_ids.length === 0) { toast.error("Pilih setidaknya satu anggota"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        // Only managers or creators can edit all fields, otherwise just status/deadline/etc.
        const payload = canEditFields ? form : {
          title: form.title, description: form.description,
          status: form.status, deadline: form.deadline, project: form.project,
          assignee_ids: form.assignee_ids,
        };
        await API.put(`/tasks/${task.id}`, payload);
        toast.success("Tugas berhasil diperbarui");
      } else {
        await API.post("/tasks", form);
        toast.success("Tugas berhasil dibuat");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSendComment = async () => {
    if (!newComment.trim()) return;
    setSendingComment(true);
    try {
      await API.post(`/tasks/${task.id}/comments`, { content: newComment });
      setNewComment("");
      setShowMentions(false);
      fetchComments();
      toast.success("Komentar dikirim");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingComment(false);
    }
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    // Mention detection logic
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    
    // Find the last word before the cursor
    // We use regex to match the current word being typed
    const match = textBeforeCursor.match(/(?:^|\s)(@[^\s]*)$/);

    if (match) {
      const mentionText = match[1]; // includes the @
      setShowMentions(true);
      setMentionQuery(mentionText.substring(1).toLowerCase());
      setMentionIndex(0); // Reset selection
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (userName) => {
    const cursorPosition = textareaRef.current?.selectionStart || newComment.length;
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const textAfterCursor = newComment.substring(cursorPosition);
    
    // Find the @ part to replace before the cursor
    const match = textBeforeCursor.match(/(?:^|\s)(@[^\s]*)$/);
    
    if (match) {
      const matchIndex = match.index;
      const spacePrefix = match[0].startsWith(' ') ? ' ' : '';
      
      const newTextBeforeCursor = textBeforeCursor.substring(0, matchIndex) + spacePrefix + `@${userName} `;
      
      setNewComment(newTextBeforeCursor + textAfterCursor);
      setShowMentions(false);
      
      // Refocus textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPos = newTextBeforeCursor.length;
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await API.delete(`/comments/${commentId}`);
      fetchComments();
    } catch { toast.error("Gagal menghapus komentar"); }
  };

  // Developers assignable list
  const developers = users.filter((u) => {
    // Everyone can assign tasks to themselves
    if (u.id === user?.id) return true;
    
    if (["superadmin", "kepala_tik", "kapokja", "ka_div"].includes(user?.role)) {
      return u.role === "staf" || u.role === "ka_div" || u.role === "kapokja" || u.role === "kepala_tik";
    }
    return u.role === "staf";
  });

  // Filter out the current user and match query
  const filteredMentionUsers = users.filter(u => 
    u.id !== user?.id && 
    u.name.toLowerCase().includes(mentionQuery)
  );

  const handleKeyDown = (e) => {
    if (!showMentions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(prev => Math.min(prev + 1, filteredMentionUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredMentionUsers.length > 0 && mentionIndex >= 0) {
        e.preventDefault();
        handleMentionSelect(filteredMentionUsers[mentionIndex].name);
      }
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const withMarkdownLineBreaks = (text) => {
    if (!text) return "";
    const normalized = String(text).replace(/\r\n/g, "\n");
    const parts = normalized.split("```");
    return parts
      .map((part, idx) => (idx % 2 === 1 ? part : part.replace(/\n/g, "  \n")))
      .join("```");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900 rounded-lg shadow-2xl border border-slate-200 dark:border-slate-800 p-0 max-h-[85vh] overflow-hidden flex flex-col" data-testid="task-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg font-semibold dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEdit ? "Detail Tugas" : "Buat Tugas Baru"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
            {isEdit
              ? (canEdit ? "Perbarui detail tugas atau tambahkan komentar" : "Lihat detail tugas dan tambahkan komentar")
              : "Isi detail untuk membuat tugas baru"}
          </DialogDescription>
        </DialogHeader>

        {isEdit ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="mx-6 grid grid-cols-2 bg-slate-100 dark:bg-slate-800 rounded-lg h-9">
              <TabsTrigger value="details" className="text-xs rounded-md dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-white dark:text-slate-400" data-testid="tab-details">Detail</TabsTrigger>
              <TabsTrigger value="comments" className="text-xs rounded-md dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-white dark:text-slate-400" data-testid="tab-comments">
                <MessageSquare className="w-3.5 h-3.5 mr-1" />
                Komentar ({comments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="flex-1 overflow-y-auto px-6 pb-6 mt-2">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="task-form">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                    Judul
                    {!isManager && isEdit && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                  </Label>
                  <Input
                    placeholder="Judul tugas"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 rounded-md dark:text-slate-200 ${!canEditFields ? 'opacity-60' : ''}`}
                    disabled={!canEditFields}
                    data-testid="task-title-input"
                  />
                </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        Deskripsi
                        {!canEditFields && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                      </span>
                      <span className="text-[10px] font-normal text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">Mendukung Markdown</span>
                    </Label>
                    <Textarea
                      placeholder="Deskripsikan tugas..."
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={`min-h-[120px] bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 rounded-md dark:text-slate-200 ${!canEditFields ? 'opacity-60' : ''}`}
                      disabled={!canEditFields}
                      data-testid="task-description-textarea"
                    />
                    {/* Markdown Preview if not empty */}
                    {form.description && (
                      <div className="mt-2 text-xs border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/50 p-3 rounded-md prose prose-sm dark:prose-invert max-w-none prose-p:leading-snug prose-li:my-0.5">
                        <div className="font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 text-[10px]">Pratinjau</div>
                        <ReactMarkdown>{form.description}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        Nama Proyek / Aplikasi
                        {!canEditFields && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                      </span>
                      {canEditFields && !showNewProject && (
                        <button 
                          type="button" 
                          onClick={() => setShowNewProject(true)} 
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold"
                          data-testid="task-new-project-btn"
                        >
                          + Proyek Baru
                        </button>
                      )}
                    </Label>
                    
                    {showNewProject ? (
                      <div className="flex items-center gap-2">
                        <Input
                          autoFocus
                          placeholder="Nama aplikasi..."
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 h-9 dark:text-slate-200"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateProject())}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          onClick={handleCreateProject} 
                          disabled={creatingProject || !newProjectName.trim()} 
                          className="bg-blue-600 dark:bg-blue-500 text-white h-9"
                        >
                          {creatingProject ? <Loader2 className="w-3 h-3 animate-spin" /> : "Simpan"}
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setShowNewProject(false)} 
                          className="h-9 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Batal
                        </Button>
                      </div>
                    ) : (
                      <Select
                        value={form.project}
                        onValueChange={(val) => setForm({ ...form, project: val })}
                        disabled={!canEditFields}
                      >
                        <SelectTrigger className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md ${!canEditFields ? 'opacity-60' : ''}`} data-testid="task-project-select">
                          <SelectValue placeholder="Pilih proyek atau aplikasi" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                          {/* Built-in default options combined with db projects */}
                          {Array.from(new Set([...dbProjects.map(p => p.name)])).sort().map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      Ditugaskan Kepada
                      {!isManager && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          disabled={!isManager}
                          className={`w-full justify-between font-normal h-auto min-h-[2.5rem] py-2 bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 ${!isManager ? 'opacity-60' : ''}`}
                        >
                          {form.assignee_ids.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-20px)]">
                              {form.assignee_ids.map(id => {
                                const u = developers.find(d => d.id === id);
                                return u ? <span key={id} className="bg-slate-100 dark:bg-slate-800 text-xs px-1.5 py-0.5 rounded truncate max-w-[120px]">{u.name}</span> : null;
                              })}
                            </div>
                          ) : (
                            <span className="text-slate-500">Pilih anggota</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0 dark:bg-slate-800 dark:border-slate-700">
                        <Command className="dark:bg-slate-800">
                          <CommandInput placeholder="Cari anggota..." className="dark:text-slate-200" />
                          <CommandList>
                            <CommandEmpty>Anggota tidak ditemukan.</CommandEmpty>
                            <CommandGroup>
                              {developers.map((u) => (
                                <CommandItem
                                  key={u.id}
                                  value={u.name}
                                  onSelect={() => {
                                    setForm(prev => {
                                      const ids = prev.assignee_ids.includes(u.id)
                                        ? prev.assignee_ids.filter(id => id !== u.id)
                                        : [...prev.assignee_ids, u.id];
                                      return { ...prev, assignee_ids: ids };
                                    });
                                  }}
                                  className="dark:text-slate-200 cursor-pointer"
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${form.assignee_ids.includes(u.id) ? "opacity-100" : "opacity-0"}`}
                                  />
                                  {u.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      Prioritas
                      {!canEditFields && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                    </Label>
                    <Select
                      value={form.priority}
                      onValueChange={(val) => setForm({ ...form, priority: val })}
                      disabled={!canEditFields}
                    >
                      <SelectTrigger className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md ${!canEditFields ? 'opacity-60' : ''}`} data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                        <SelectItem value="low">Rendah</SelectItem>
                        <SelectItem value="medium">Sedang</SelectItem>
                        <SelectItem value="high">Tinggi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      Status
                      {!canEdit && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                    </Label>
                    <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })} disabled={!canEdit}>
                      <SelectTrigger className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md ${!canEdit ? 'opacity-60' : ''}`} data-testid="task-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="review">Review</SelectItem>
                        <SelectItem value="done">Selesai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      Tenggat Waktu
                      {!canEditFields && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                    </Label>
                    <Input
                      type="date"
                      value={form.deadline}
                      onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                      className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md ${!canEditFields ? 'opacity-60' : ''} dark:[color-scheme:dark]`}
                      disabled={!canEditFields}
                      data-testid="task-deadline-input"
                    />
                  </div>
                </div>
                {canEdit && (
                  <DialogFooter className="pt-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800" data-testid="task-cancel-button">
                      Batal
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white" data-testid="task-save-button">
                      {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Perbarui Tugas
                    </Button>
                  </DialogFooter>
                )}
              </form>
            </TabsContent>

            <TabsContent value="comments" className="flex-1 overflow-hidden flex flex-col px-6 pb-4 mt-2">
              <div className="flex-1 overflow-y-auto space-y-3 mb-3 min-h-[200px] max-h-[350px]" data-testid="comments-list">
                {comments.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-slate-400 dark:text-slate-500">
                    Belum ada komentar. Mulai percakapan!
                  </div>
                ) : (
                  comments.map((c) => {
                    const isMe = user?.id === c.user_id;
                    return (
                      <div key={c.id} className={`group flex gap-3 ${isMe ? 'flex-row-reverse' : 'flex-row'}`} data-testid={`comment-${c.id}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-1 overflow-hidden
                          ${isMe ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                          {c.user?.picture ? (
                            <img 
                              src={c.user.picture.startsWith('http') ? c.user.picture : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${c.user.picture}`} 
                              alt={c.user_name} 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            (c.user_name || "U").split(" ").map(n => n && n[0]).join("").toUpperCase().slice(0, 2)
                          )}
                        </div>
                        <div className={`flex flex-col min-w-0 max-w-[85%] ${isMe ? 'items-end' : 'items-start'}`}>
                          <div className={`flex items-baseline gap-2 mb-1 px-1 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{isMe ? 'Anda' : (c.user?.name || c.user_name)}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">
                              {new Date(c.created_at).toLocaleString("id-ID", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <div className={`relative flex items-center group/bubble ${isMe ? 'flex-row-reverse' : 'flex-row'} max-w-full`}>
                            <div className={`px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words overflow-x-auto shadow-sm prose prose-sm dark:prose-invert max-w-full prose-p:leading-snug prose-li:my-0.5
                              ${isMe 
                                ? 'bg-blue-600 text-white dark:bg-blue-500 rounded-tr-sm' 
                                : 'bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-200 rounded-tl-sm border border-slate-200 dark:border-slate-700'}`}
                            >
                              <ReactMarkdown>{withMarkdownLineBreaks(c.content)}</ReactMarkdown>
                            </div>
                            
                            {(isManager || isMe) && (
                              <button
                                onClick={() => handleDeleteComment(c.id)}
                                className="opacity-0 group-hover/bubble:opacity-100 transition-opacity p-1.5 rounded-full bg-white dark:bg-slate-700 shadow-sm border border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-300 hover:text-red-500 dark:hover:text-red-400 z-10 shrink-0 mx-2"
                                title="Hapus komentar"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <div className="relative flex-1">
                  <Textarea
                    ref={textareaRef}
                    placeholder="Tulis komentar... (Ketik @Nama untuk menyebut seseorang)"
                    value={newComment}
                    onChange={handleTextareaChange}
                    onKeyDown={(e) => {
                      handleKeyDown(e);
                      if (e.key === "Enter" && !e.shiftKey && !showMentions) {
                        e.preventDefault();
                        handleSendComment();
                      }
                    }}
                    rows={2}
                    className="w-full bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 rounded-md dark:text-slate-200 min-h-[44px] max-h-40 resize-y"
                    data-testid="comment-input"
                  />
                  
                  {/* Mentions Dropdown */}
                  {showMentions && filteredMentionUsers.length > 0 && (
                    <div className="absolute bottom-full left-0 mb-1 w-64 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-50">
                      <div className="p-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
                        Sebut rekan tim
                      </div>
                      {filteredMentionUsers.map((u, i) => (
                        <div
                          key={u.id}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 ${i === mentionIndex ? 'bg-blue-50 dark:bg-slate-700' : ''}`}
                          onClick={() => handleMentionSelect(u.name)}
                          onMouseEnter={() => setMentionIndex(i)}
                        >
                          <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-600 flex-shrink-0 flex items-center justify-center text-slate-600 dark:text-slate-300 font-medium text-xs">
                            {u.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white leading-none">{u.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{u.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={handleSendComment}
                  disabled={sendingComment || !newComment.trim()}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-3"
                  data-testid="send-comment-button"
                >
                  {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          /* Create mode - no tabs */
          <div className="px-6 pb-6 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="task-form">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Judul
                </Label>
                <Input
                  placeholder="Judul Tugas"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 rounded-md dark:text-slate-200`}
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Deskripsi
                </Label>
                <Textarea
                  placeholder="Deskripsikan tugas..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 rounded-md min-h-[80px] dark:text-slate-200`}
                  data-testid="task-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    Nama Proyek / Aplikasi
                  </span>
                  {!showNewProject && (
                    <button 
                      type="button" 
                      onClick={() => setShowNewProject(true)} 
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold"
                    >
                      + Proyek Baru
                    </button>
                  )}
                </Label>
                
                {showNewProject ? (
                  <div className="flex items-center gap-2">
                    <Input
                      autoFocus
                      placeholder="Nama aplikasi..."
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 h-9 dark:text-slate-200"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateProject())}
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={handleCreateProject} 
                      disabled={creatingProject || !newProjectName.trim()} 
                      className="bg-blue-600 dark:bg-blue-500 text-white h-9"
                    >
                      {creatingProject ? <Loader2 className="w-3 h-3 animate-spin" /> : "Simpan"}
                    </Button>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setShowNewProject(false)} 
                      className="h-9 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      Batal
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={form.project}
                    onValueChange={(val) => setForm({ ...form, project: val })}
                  >
                    <SelectTrigger className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md`} data-testid="task-project-select">
                      <SelectValue placeholder="Pilih proyek atau aplikasi" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                      {Array.from(new Set([...dbProjects.map(p => p.name)])).sort().map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                    Ditugaskan Kepada
                    {!isManager && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" />}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={!isManager}
                        className={`w-full justify-between font-normal h-auto min-h-[2.5rem] py-2 bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 ${!isManager ? 'opacity-60' : ''}`}
                      >
                        {form.assignee_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-20px)]">
                            {form.assignee_ids.map(id => {
                              const u = developers.find(d => d.id === id);
                              return u ? <span key={id} className="bg-slate-100 dark:bg-slate-800 text-xs px-1.5 py-0.5 rounded truncate max-w-[120px]">{u.name}</span> : null;
                            })}
                          </div>
                        ) : (
                          <span className="text-slate-500">Pilih Anggota</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0 dark:bg-slate-800 dark:border-slate-700">
                      <Command className="dark:bg-slate-800">
                        <CommandInput placeholder="Cari anggota..." className="dark:text-slate-200" />
                        <CommandList>
                          <CommandEmpty>No assignee found.</CommandEmpty>
                          <CommandGroup>
                            {developers.map((u) => (
                              <CommandItem
                                key={u.id}
                                value={u.name}
                                onSelect={() => {
                                  setForm(prev => {
                                    const ids = prev.assignee_ids.includes(u.id)
                                      ? prev.assignee_ids.filter(id => id !== u.id)
                                      : [...prev.assignee_ids, u.id];
                                    return { ...prev, assignee_ids: ids };
                                  });
                                }}
                                className="dark:text-slate-200 cursor-pointer"
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${form.assignee_ids.includes(u.id) ? "opacity-100" : "opacity-0"}`}
                                />
                                {u.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                    Prioritas
                  </Label>
                  <Select value={form.priority} onValueChange={(val) => setForm({ ...form, priority: val })}>
                    <SelectTrigger className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md`} data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                      <SelectItem value="low">Rendah</SelectItem>
                      <SelectItem value="medium">Sedang</SelectItem>
                      <SelectItem value="high">Tinggi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Status</Label>
                  <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                    <SelectTrigger className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md" data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="done">Selesai</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Tenggat Waktu
                  </Label>
                  <Input
                    type="date"
                    value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    className={`bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md dark:[color-scheme:dark]`}
                    data-testid="task-deadline-input"
                  />
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800" data-testid="task-cancel-button">
                  Batal
                </Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white" data-testid="task-save-button">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Buat Tugas
                </Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
